import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { organizationsService } from '@/services/organizations';
import type { Organization, OrganizationMember } from '@/types';

interface OrganizationState {
  organizations: Organization[];
  currentOrganization: Organization | null;
  members: OrganizationMember[];
  userRole: string | null;
  isLoading: boolean;
  error: string | null;

  fetchOrganizations: () => Promise<void>;
  setCurrentOrganization: (org: Organization | null) => void;
  fetchCurrentOrganization: (id: string) => Promise<void>;
  createOrganization: (name: string) => Promise<Organization>;
  updateOrganization: (id: string, data: Partial<Organization>) => Promise<void>;
  deleteOrganization: (id: string) => Promise<void>;
  fetchMembers: (orgId: string) => Promise<void>;
  addMember: (orgId: string, userId: string, role: string) => Promise<void>;
  updateMemberRole: (orgId: string, userId: string, role: string) => Promise<void>;
  removeMember: (orgId: string, userId: string) => Promise<void>;
  clearError: () => void;
}

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      organizations: [],
      currentOrganization: null,
      members: [],
      userRole: null,
      isLoading: false,
      error: null,

      fetchOrganizations: async () => {
        set({ isLoading: true, error: null });
        try {
          const organizations = await organizationsService.list();
          set({ organizations, isLoading: false });

          // Set current organization if none selected
          const current = get().currentOrganization;
          if (!current && organizations.length > 0) {
            set({ currentOrganization: organizations[0] });
          }
        } catch (error) {
          set({ error: 'Failed to load organizations', isLoading: false });
        }
      },

      setCurrentOrganization: (org) => {
        set({ currentOrganization: org, members: [], userRole: null });
      },

      fetchCurrentOrganization: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const { organization, role } = await organizationsService.get(id);
          set({
            currentOrganization: organization,
            userRole: role,
            isLoading: false,
          });

          // Update in list
          const organizations = get().organizations.map((org) =>
            org.id === id ? organization : org
          );
          set({ organizations });
        } catch (error) {
          set({ error: 'Failed to load organization', isLoading: false });
        }
      },

      createOrganization: async (name: string) => {
        set({ isLoading: true, error: null });
        try {
          const org = await organizationsService.create({ name });
          set((state) => ({
            organizations: [...state.organizations, org],
            currentOrganization: org,
            userRole: 'owner',
            isLoading: false,
          }));
          return org;
        } catch (error) {
          set({ error: 'Failed to create organization', isLoading: false });
          throw error;
        }
      },

      updateOrganization: async (id: string, data: Partial<Organization>) => {
        set({ isLoading: true, error: null });
        try {
          const updated = await organizationsService.update(id, data);
          set((state) => ({
            organizations: state.organizations.map((org) =>
              org.id === id ? updated : org
            ),
            currentOrganization:
              state.currentOrganization?.id === id ? updated : state.currentOrganization,
            isLoading: false,
          }));
        } catch (error) {
          set({ error: 'Failed to update organization', isLoading: false });
          throw error;
        }
      },

      deleteOrganization: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await organizationsService.delete(id);
          const organizations = get().organizations.filter((org) => org.id !== id);
          set({
            organizations,
            currentOrganization:
              get().currentOrganization?.id === id
                ? organizations[0] || null
                : get().currentOrganization,
            isLoading: false,
          });
        } catch (error) {
          set({ error: 'Failed to delete organization', isLoading: false });
          throw error;
        }
      },

      fetchMembers: async (orgId: string) => {
        try {
          const members = await organizationsService.getMembers(orgId);
          set({ members });
        } catch (error) {
          set({ error: 'Failed to load members' });
        }
      },

      addMember: async (orgId: string, userId: string, role: string) => {
        try {
          const member = await organizationsService.addMember(orgId, userId, role);
          set((state) => ({ members: [...state.members, member] }));
        } catch (error) {
          set({ error: 'Failed to add member' });
          throw error;
        }
      },

      updateMemberRole: async (orgId: string, userId: string, role: string) => {
        try {
          const updated = await organizationsService.updateMemberRole(orgId, userId, role);
          set((state) => ({
            members: state.members.map((m) =>
              m.user_id === userId ? updated : m
            ),
          }));
        } catch (error) {
          set({ error: 'Failed to update member role' });
          throw error;
        }
      },

      removeMember: async (orgId: string, userId: string) => {
        try {
          await organizationsService.removeMember(orgId, userId);
          set((state) => ({
            members: state.members.filter((m) => m.user_id !== userId),
          }));
        } catch (error) {
          set({ error: 'Failed to remove member' });
          throw error;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'statsmatic-organization',
      partialize: (state) => ({
        currentOrganization: state.currentOrganization,
      }),
    }
  )
);

export default useOrganizationStore;
